/**
 * What File Uploads offers for each class and kind (IP-103; design
 * 2026-10-02 sections 3 and 8): six classes, the four Core data ones each
 * DAILY or INITIAL (@lfoc/domain/intake/kinds.js, the leaf module the
 * catalogue validates INTAKE_UPLOAD by), and the date each takes - the
 * server's own rule (intake/classes/contract.js `dates`), so the dialog
 * never offers a date the upload would refuse:
 *
 *   NAV_VALUES, BANK_STATEMENT   NOT_FUTURE    the business date, today or
 *                                              earlier (as before IP-103)
 *   any DAILY                    TODAY         locked to today in Colombo,
 *                                              at upload and again at accept
 *                                              (DAILY_NOT_TODAY)
 *   any INITIAL                  BEFORE_TODAY  the "As-at date": strictly
 *                                              before today
 *                                              (INITIAL_AS_AT_NOT_PAST) -
 *                                              history is loaded before the
 *                                              Core deals; today is the
 *                                              daily path's
 *
 * INITIAL is offered only with core.intake.initial (SCREEN_RULES
 * uploads.initial), to upload and - with accept's own claim - to accept or
 * reject (mayDecide). TRANSACTIONS DAILY's dealing claims
 * (core.order.review to upload, core.order.approve to accept) are the
 * server's to judge: it refuses LOAD_NOT_PERMITTED (403), naming the claim.
 */
import {
  CORE_DATA_CLASSES, FILE_CLASSES, LOAD_KINDS, STORED_CLASSES, SYSTEM_ACCEPTOR, WM_CLASSES, acceptedBySystem, takesLoadKind,
} from '@lfoc/domain/intake/kinds.js';

export { CORE_DATA_CLASSES, FILE_CLASSES as FILE_CLASS_KEYS, LOAD_KINDS, STORED_CLASSES, SYSTEM_ACCEPTOR, WM_CLASSES, acceptedBySystem, takesLoadKind };

/** A class in a sentence or a list cell. */
export const CLASS_LABEL = Object.freeze({
  NAV_VALUES: 'NAV values',
  BANK_STATEMENT: 'Bank statement',
  CALENDAR: 'Calendar',
  UNIT_PRICES: 'Unit prices',
  HOLDINGS: 'Holdings',
  TRANSACTIONS: 'Transactions',
  // Migration 0026: the wealth-managed clients' files, kept as uploaded.
  WM_HOLDINGS: 'WM Holdings',
  WM_TRANSACTIONS: 'WM Transactions',
  // Migration 0014: the analytics files, kept as uploaded.
  BENCHMARK: 'Daily benchmark',
  INCOME_EXPENSE: 'Income and expense breakdown',
  SECURITIES_UTDOF: 'UTDOF security holdings',
  SECURITIES_UTIF: 'UTIF security holdings',
  SECURITIES_UTLMF: 'UTLMF security holdings',
  SECURITIES_UTOYF: 'UTOYF security holdings',
  FUND_FACTS_UTDOF: 'UTDOF fund facts',
  FUND_FACTS_UTIF: 'UTIF fund facts',
  FUND_FACTS_UTLMF: 'UTLMF fund facts',
  FUND_FACTS_UTOYF: 'UTOYF fund facts',
});

export const KIND_LABEL = Object.freeze({ DAILY: 'Daily', INITIAL: 'Initial load' });

/**
 * The File category's groups, as the drop zone's own words group them - for
 * the category dropdown and the "Download template" menu alike
 * (stagedFiles.js classOptions, templates.js templateGroups): each lists its
 * classes, or for a fund's analytics files the prefix a fund's id completes.
 */
export const CLASS_GROUPS = Object.freeze([
  { label: 'Fund files', classes: ['NAV_VALUES', 'BANK_STATEMENT'] },
  { label: 'Core data', classes: ['CALENDAR', 'UNIT_PRICES', 'HOLDINGS', 'TRANSACTIONS'] },
  // Migration 0026: the wealth-managed clients' files, kept as uploaded - daily or an initial load.
  { label: 'Wealth managed files', classes: ['WM_HOLDINGS', 'WM_TRANSACTIONS'] },
  // Migration 0014: the analytics files, kept as uploaded - daily, monthly and ad hoc, as they are exported.
  { label: 'Daily files', classes: ['BENCHMARK', 'INCOME_EXPENSE'] },
  { label: 'Monthly files', fundPrefix: 'SECURITIES_', fundNoun: 'security holdings' },
  { label: 'Ad hoc files', fundPrefix: 'FUND_FACTS_', fundNoun: 'fund facts' },
]);

/** The File category group a class is listed under, or null. */
export function groupOf(fileClass) {
  return CLASS_GROUPS.find((g) => (g.fundPrefix ? String(fileClass).startsWith(g.fundPrefix) : g.classes.includes(fileClass)))?.label ?? null;
}

/** The kinds a Core data class is uploaded as: Daily or Initial load, Unit prices as its initial load (price history) alone. */
const CLASS_KINDS = Object.freeze({ UNIT_PRICES: Object.freeze(['INITIAL']) });

/** The kinds the dialog offers for a class: its own (CLASS_KINDS), Initial load only to a holder of core.intake.initial. */
export function kindOptions(canInitial, fileClass = null) {
  const kinds = CLASS_KINDS[fileClass] ?? LOAD_KINDS;
  return [
    { value: 'DAILY', label: KIND_LABEL.DAILY },
    ...(canInitial ? [{ value: 'INITIAL', label: KIND_LABEL.INITIAL }] : []),
  ].filter((o) => kinds.includes(o.value));
}

/** The list's Kind filter: each sends GET intake/batches?loadKind= (ALL sends none). */
export const KIND_FILTERS = Object.freeze([
  { value: 'ALL', label: 'All Types' },
  { value: 'DAILY', label: KIND_LABEL.DAILY },
  { value: 'INITIAL', label: KIND_LABEL.INITIAL },
]);

/** The list's query for a Kind filter: the first page, or with a cursor (the last nextCursor) the next. */
export function batchesQuery(kindFilter, limit = 100, cursor = null) {
  return { limit, loadKind: LOAD_KINDS.includes(kindFilter) ? kindFilter : undefined, cursor: cursor || undefined };
}

/** A batch's kind in the list: a NAV file and a statement have none. */
export function kindLabelOf(batch) {
  return batch?.loadKind ? (KIND_LABEL[batch.loadKind] ?? batch.loadKind) : '—';
}

/** A batch's class and kind in a sentence: "Holdings (Initial load)". */
export function classLabelOf(batch) {
  const name = CLASS_LABEL[batch?.fileClass] ?? batch?.fileClass ?? 'File';
  return batch?.loadKind ? name + ' (' + kindLabelOf(batch).toLowerCase() + ')' : name;
}

/** The date rule of a class and kind (contract.js dates), and the label the dialog gives its date. */
export function dateRule(fileClass, loadKind) {
  if (!takesLoadKind(fileClass)) return { rule: 'NOT_FUTURE', label: 'Business date' };
  if (loadKind === 'INITIAL') return { rule: 'BEFORE_TODAY', label: 'As-at date' };
  return { rule: 'TODAY', label: 'Business date (today)' };
}

/** The day before a YYYY-MM-DD date. */
export function dayBefore(ymd) {
  const at = new Date(ymd + 'T00:00:00Z');
  at.setUTCDate(at.getUTCDate() - 1);
  return at.toISOString().slice(0, 10);
}

/** Whether a date is one the rule takes, against today (YYYY-MM-DD, Colombo). */
export function dateAllowed(rule, ymd, today) {
  if (!ymd) return false;
  switch (rule) {
    case 'TODAY':
      return ymd === today;
    case 'BEFORE_TODAY':
      return ymd < today;
    default:
      return ymd <= today;
  }
}

/** The date the dialog holds once the class or kind changes: the one chosen while the rule takes it, else the rule's own default. */
export function dateFor(rule, current, today) {
  if (dateAllowed(rule, current, today)) return current;
  return rule === 'BEFORE_TODAY' ? dayBefore(today) : today;
}

/** Why a date will not do for the rule, in words for the dialog - or null. */
export function dateProblem(rule, ymd, today) {
  if (dateAllowed(rule, ymd, today)) return null;
  switch (rule) {
    case 'TODAY':
      return 'A daily file is for today (' + today + ') only.';
    case 'BEFORE_TODAY':
      return 'An initial load\'s as-at date must be before today (' + today + ').';
    default:
      return 'The business date cannot be after today (' + today + ').';
  }
}

/**
 * Whether this person is offered Accept and Reject on a batch: accept's claim,
 * and for an INITIAL load also core.intake.initial (design 8). The uploader
 * is refused by the server (MAKER_IS_CHECKER) and told so by the drawer.
 * Never on a price file - NAV values: the system accepts
 * it once its scan passes, and each NAV run's approval on Unit Prices is the
 * second pair of eyes (migration 0016; the server refuses ACCEPTED_BY_SYSTEM).
 */
export function mayDecide(batch, { canAccept, canInitial }) {
  if (acceptedBySystem(batch)) return false;
  return Boolean(canAccept) && (batch?.loadKind !== 'INITIAL' || Boolean(canInitial));
}

/**
 * What accepting a file of this class and kind does (D-A..D-C; design
 * sections 4.1-4.7), in a sentence the dialog shows under the kind - so the
 * uploader knows before choosing a code.
 */
const LOAD_HINT = Object.freeze({
  NAV_VALUES: 'The NAV for the business date of one or more active funds: once its scan passes, a NAV run is made for each fund in it, for another person to approve.',
  BANK_STATEMENT: 'A fund bank account\'s statement - a collection account\'s, or a payout account\'s for redemption payments: accepting it imports'
    + ' the lines and matches what it can.',
  'CALENDAR:DAILY': 'Calendar rows for dates after today: new dates are added and changed ones updated; a date anything was dealt on cannot change.',
  'CALENDAR:INITIAL': 'The calendar\'s history: new dates are added and changed ones updated; a date anything was dealt on cannot change.',
  'UNIT_PRICES:INITIAL': 'Price history, dated before the Core first dealt: kept as imported prices, never used to price a transaction.',
  'HOLDINGS:DAILY': 'LIV\'s daily-client-holdings: compared with the register once accepted, and the breaks listed. Balances never change.',
  'HOLDINGS:INITIAL': 'Opening lots, a row per lot, for clients who hold nothing in the fund yet: accepting posts them to the register.',
  'TRANSACTIONS:DAILY': 'LIV\'s daily-transactions: posted to the register for today once accepted, skipping any the Core already has. '
    // The permissions by the names the Roles screen gives them (authority/claims.js CLAIM_TEXT), never their codes.
    + 'Uploading also needs the Review transactions permission, and accepting needs Approve transactions.',
  'TRANSACTIONS:INITIAL': 'Transaction history up to the as-at date: kept as history; the register is not changed.',
});

/** The store-only classes' sentence (migration 0014), and the WM files' (0026): the file is kept, and nothing is loaded from it. */
const STORED_HINT = 'Kept as uploaded once accepted, for download. Nothing is loaded from it into the Core.';

/** The dialog's sentence for a class and kind, or null while a Core data class has no kind yet. */
export function loadHint(fileClass, loadKind) {
  const key = takesLoadKind(fileClass) ? (loadKind ? fileClass + ':' + loadKind : null) : fileClass;
  return key ? (LOAD_HINT[key] ?? (STORED_CLASSES.includes(fileClass) || WM_CLASSES.includes(fileClass) ? STORED_HINT : null)) : null;
}
